import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { DollarSign, TrendingUp, TrendingDown, UserPlus, UserMinus, Users, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const usd = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Delta({ cur, prev, money, invert }) {
  const d = (cur || 0) - (prev || 0);
  if (!d) return <span className="text-xs text-gray-500">same as last month</span>;
  const good = invert ? d < 0 : d > 0;
  const Icon = d > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={"text-xs inline-flex items-center gap-1 " + (good ? "text-emerald-400" : "text-red-400")}>
      <Icon className="h-3.5 w-3.5" /> {d > 0 ? "+" : "−"}{money ? usd(Math.abs(d)) : Math.abs(d)} vs last month
    </span>
  );
}

function Kpi({ testid, icon: Icon, label, value, sub, children }) {
  return (
    <div data-testid={testid} className="bg-card border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-gray-500 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-amber-400" /> {label}</p>
      <p data-testid={`${testid}-value`} className="text-2xl font-display font-bold mt-1.5 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function RevenueDashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (refresh) => {
    setBusy(true);
    api.get("/admin/billing/revenue", { params: refresh ? { refresh: 1 } : {} }).then((r) => setD(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { load(false); }, []);

  if (!d) return <div className="mt-6 bg-card border border-gray-800 rounded-2xl p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;

  return (
    <section data-testid="revenue-dashboard" className="mt-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold">Revenue</h2>
        <button data-testid="revenue-refresh" onClick={() => load(true)} disabled={busy} className="text-xs text-gray-400 hover:text-amber-300 inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh from Stripe
        </button>
      </div>
      {d.stripe_error && <p data-testid="revenue-stripe-error" className="text-xs text-amber-300 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {d.stripe_error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <Kpi testid="kpi-total-mrr" icon={DollarSign} label="Monthly recurring revenue" value={usd(d.total_mrr)} sub="Stripe + estimated existing/comped">
          <Delta cur={d.total_mrr} prev={d.prev_total_mrr} money />
        </Kpi>
        <Kpi testid="kpi-stripe-mrr" icon={DollarSign} label="Live from Stripe" value={usd(d.stripe_mrr)} sub={`${d.stripe_paying} paying${d.in_trial ? ` · ${d.in_trial} in trial` : ""}`}>
          <Delta cur={d.stripe_mrr} prev={d.prev_stripe_mrr} money />
        </Kpi>
        <Kpi testid="kpi-legacy-mrr" icon={Users} label="Existing & comped (estimate)" value={usd(d.legacy_mrr)} sub={`${d.legacy_paying} churches at plan price${d.no_plan_free ? ` · ${d.no_plan_free} with no plan` : ""}`} />
        <Kpi testid="kpi-new-subs" icon={UserPlus} label="New subscribers this month" value={d.new_this_month} sub={`${d.new_last_month} last month`}>
          <Delta cur={d.new_this_month} prev={d.new_last_month} />
        </Kpi>
        <Kpi testid="kpi-cancellations" icon={UserMinus} label="Cancellations this month" value={d.cancellations_this_month} sub={`${d.cancellations_last_month} last month`}>
          <Delta cur={d.cancellations_this_month} prev={d.cancellations_last_month} invert />
        </Kpi>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <div data-testid="revenue-chart" className="lg:col-span-2 bg-card border border-gray-800 rounded-2xl p-5">
          <p className="text-sm font-semibold mb-3">Last 12 months</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.months} margin={{ left: 4, right: 4, top: 4 }}>
                <CartesianGrid stroke="#1f2937" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#9ca3af", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(m) => m.split(" ")[0]} />
                <YAxis yAxisId="mrr" tick={{ fill: "#9ca3af", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} width={56} />
                <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} tick={{ fill: "#9ca3af", fontSize: 11 }} tickLine={false} axisLine={false} width={28} />
                <Tooltip contentStyle={{ background: "#0b0f17", border: "1px solid #1f2937", borderRadius: 10, fontSize: 12 }}
                  formatter={(v, n) => (n.includes("MRR") ? usd(v) : v)} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#9ca3af" }} />
                <Bar yAxisId="mrr" dataKey="stripe_mrr" name="Stripe MRR" stackId="m" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="mrr" dataKey="legacy_mrr" name="Existing/comped MRR" stackId="m" fill="#78350f" radius={[4, 4, 0, 0]} />
                <Line yAxisId="cnt" type="monotone" dataKey="new_subscribers" name="New" stroke="#34d399" strokeWidth={2} dot={{ r: 2 }} />
                <Line yAxisId="cnt" type="monotone" dataKey="cancellations" name="Cancelled" stroke="#f87171" strokeWidth={2} dot={{ r: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div data-testid="revenue-by-plan" className="bg-card border border-gray-800 rounded-2xl p-5">
          <p className="text-sm font-semibold">By plan</p>
          <p data-testid="kpi-arpc" className="text-xs text-gray-500 mt-1">Average revenue per church: <span className="text-white font-semibold">{usd(d.arpc)}</span>/mo</p>
          <div className="mt-4 space-y-3">
            {d.by_plan.map((p) => {
              const total = p.stripe_mrr + p.legacy_mrr;
              const pct = d.total_mrr ? Math.round((total / d.total_mrr) * 100) : 0;
              return (
                <div key={p.plan} data-testid={`plan-rev-${p.plan}`}>
                  <div className="flex justify-between text-xs"><span className="text-gray-300">{p.name}</span><span className="text-white tabular-nums">{usd(total)}</span></div>
                  <div className="h-1.5 rounded-full bg-gray-800 mt-1.5 overflow-hidden"><div className="h-full bg-amber-500 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
                  <p className="text-[10px] text-gray-500 mt-1">{p.stripe_count} on Stripe · {p.legacy_count} existing/comped</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
